import { customerIoInAppConfig, disableAnalytics } from '@config';
import { experiment, FeatureFlagKeys } from '@lib/experiment';
import { webappClient } from '@lib/bugsnag';

type Gist = (typeof import('customerio-gist-web'))['default'];

/** Light/dark as resolved by `AppThemeProvider` — `useAppTheme().resolvedMode`. */
type ResolvedThemeMode = 'light' | 'dark';

/**
 * Customer.io in-app messaging (Gist) — banners, modals, tooltips and inline
 * embeds authored in the Journeys workspace and targeted at signed-in users.
 *
 * The SDK is pulled in with a dynamic `import()` on purpose: a static import
 * lands it in `main.js`, which is close to its 219 KB budget (.size-limit.json),
 * and logged-out visitors never need it. Loading it lazily puts the ~32 KB
 * (gzipped) bundle in its own chunk that is only fetched once a signed-in user
 * is eligible.
 *
 * Boot ordering: `boot()` must be called after `experiment.identify()` resolves,
 * otherwise Flagsmith is still serving defaults. Route changes can arrive before
 * that (the router mounts first), so the latest route is stashed and replayed
 * once the SDK is up.
 */
// ts-prune-ignore-next — exported so tests can build isolated instances
export class CustomerIoInApp {
  private gist: Gist | null = null;

  /** Epoch of the boot currently in flight, or null when none is. */
  private bootingEpoch: number | null = null;

  /** Latest route seen before the SDK finished loading; replayed on boot. */
  private pendingRoute: string | null = null;

  /** Bumped by `shutdown()` to invalidate a boot that is still awaiting. */
  private bootEpoch = 0;

  /**
   * Gated on three things: analytics being on at all (local dev turns it off),
   * a configured site ID, and the rollout flag. Re-read on every call, but a
   * booted session only stops on `shutdown()` — `useCustomerIoInApp` drives both
   * directions of the flag.
   */
  private get enabled() {
    return (
      !disableAnalytics &&
      Boolean(customerIoInAppConfig.siteId) &&
      experiment.isFeatureEnabled(FeatureFlagKeys.enableCustomerIoInApp)
    );
  }

  /**
   * Loads the SDK and identifies the current user by **email**, not by the
   * RudderStack user id.
   *
   * Customer.io profiles are keyed by email: the marketing pipeline's user
   * transformation rewrites `userId` to the (lowercased) email address before
   * events reach Customer.io, and only those email-keyed profiles carry the
   * organization/workspace relationships that campaign segments target. Sending
   * the RudderStack UUID instead resolves to the orphaned, email-less profiles
   * that no segment can reach.
   *
   * Getting this wrong is invisible: an unrecognised token returns HTTP 200 with
   * an empty queue rather than an error, so messages simply never arrive.
   *
   * `setUserToken` persists the identifier in localStorage for 30 days, which is
   * why `shutdown()` on logout is not optional — without it the next person to
   * use the browser inherits the previous user's message queue.
   */
  public async boot(email: string) {
    // Keyed on the epoch, not a plain boolean: after `shutdown()` bumps it, a
    // re-login must be able to boot even while the abandoned boot is still awaiting.
    if (!this.enabled || this.gist || this.bootingEpoch === this.bootEpoch || !email) {
      return;
    }

    const epoch = this.bootEpoch;
    this.bootingEpoch = epoch;

    try {
      const { default: gist } = await import('customerio-gist-web');

      await gist.setup({
        siteId: customerIoInAppConfig.siteId,
        // 'auto' makes Gist resolve light/dark from the CSS `color-scheme` of
        // <html>, which `setColorScheme` below owns, and re-resolve via a
        // MutationObserver when it changes. 'system' would follow the OS and
        // ignore an explicit in-app theme choice.
        colorScheme: 'auto',
        // `Gist: …` console output while developing; compiles to false in prod.
        logging: process.env.NODE_ENV !== 'production',
      });

      // A logout or flag flip while the SDK was loading must win. No token was set
      // yet, so bailing out is enough.
      if (epoch !== this.bootEpoch) {
        return;
      }

      this.gist = gist;

      // The transformation lowercases before writing the profile, so an exact
      // match requires normalising here too.
      await gist.setUserToken(email.toLowerCase());

      // `shutdown()` landed between the two awaits, so it cleared before this call
      // set the token. Undo it, or the identity outlives the session by 30 days.
      if (epoch !== this.bootEpoch) {
        this.gist = null;
        await gist.clearUserToken();
        return;
      }

      if (this.pendingRoute !== null) {
        await gist.setCurrentRoute(this.pendingRoute);
        this.pendingRoute = null;
      }
    } catch (err) {
      // A failed load must never break the app — in-app messaging is additive.
      webappClient.notify(err as Error);
    } finally {
      // Only clear if a newer boot has not already claimed the slot.
      if (this.bootingEpoch === epoch) {
        this.bootingEpoch = null;
      }
    }
  }

  /**
   * Tells Gist which page the user is on so message route rules can match.
   * The SPA never reloads, so without this every message would evaluate against
   * the URL of the initial hard load.
   */
  public setRoute(route: string) {
    if (!this.gist) {
      // Not booted yet (or disabled) — keep the latest route for replay.
      this.pendingRoute = route;
      return;
    }

    // A live send supersedes the queue, or boot's replay would follow it with a stale route.
    this.pendingRoute = null;
    this.gist.setCurrentRoute(route).catch((err: Error) => webappClient.notify(err));
  }

  /**
   * Publishes the app's resolved theme as the document's CSS `color-scheme`,
   * which is the signal Gist's `colorScheme: 'auto'` reads. Only touches the DOM
   * while the integration is active so the flag stays a clean on/off — note that
   * setting `color-scheme` also makes native controls (scrollbars, form widgets)
   * follow the theme, which is correct but is a visible change on rollout.
   */
  public setColorScheme(mode: ResolvedThemeMode) {
    if (!this.enabled) {
      // Drop anything already published, so a flag flip does not leave native controls tinted.
      this.clearColorScheme();
      return;
    }

    document.documentElement.style.colorScheme = mode;
  }

  /** Hands `color-scheme` back to the stylesheet. Safe when nothing was published. */
  private clearColorScheme() {
    document.documentElement.style.removeProperty('color-scheme');
  }

  /**
   * Clears the Customer.io identity on logout or a flag flip to off. Safe to call
   * when the SDK never loaded. Nulling `gist` is what stops route forwarding.
   */
  public shutdown() {
    // Invalidates an in-flight boot, which would otherwise resume and identify a
    // user who has already left.
    this.bootEpoch += 1;
    this.pendingRoute = null;
    this.clearColorScheme();

    if (!this.gist) {
      return;
    }

    const { gist } = this;
    this.gist = null;
    gist.clearUserToken().catch((err: Error) => webappClient.notify(err));
  }
}

export const customerIoInApp = new CustomerIoInApp();
